import { Injectable } from '@angular/core';
import { IFormMasterBuilder } from '../../../form-master/form-master.interface';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ISelectOption } from '../../../form-master/components/select-field/select-field.interface';
import { TranslateService } from '@ngx-translate/core';
import { IRadioOption } from '../../../form-master/components/radio-field/radio-field.interface';
import { UtilsService } from '../../../shared/services/utils/utils.service';
import { fromJS, List, Map } from 'immutable';
import {
  PostPurchaseInfo,
  PostPurchasePriorityEnum,
  PostPurchaseStateEnum,
  PostPurchaseYearsEnum,
} from '../../../settings-data/sub-models/post-purchase-info.model';
import {
  CompanyInfo,
  NumOfEmployeesEnum,
} from '../../../settings-data/sub-models/company-info.model';
import { EmployeesRadioEnum } from '../../../settings/settings.data';
import { identity, mapValues, pickBy } from 'lodash';
import { SettingsContent } from '../../../settings-data/sub-models/settings-content.model';
import { DateTimeService } from 'i2g-ng-utils';

@Injectable()
export class PostPurchaseDialogBuilder implements IFormMasterBuilder {
  FORM_KEYS = {
    INDUSTRY: 'industry',
    SUB_INDUSTRY: 'sub_industry',
    BUSINESS_YEARS: 'business_years',
    EMPLOYEES_RADIO: 'employees_radio',
    EMPLOYEES: 'employees',
    WHAT_MATTERS_MOST: 'what_matters_most',
  };

  constructor(
    private _formBuilder: FormBuilder,
    private _utilsService: UtilsService,
    private _dateTimeService: DateTimeService,
    private _translateService: TranslateService
  ) {}

  buildForm(
    companyInfo: CompanyInfo,
    postPurchaseInfo: PostPurchaseInfo
  ): FormGroup {
    return this._formBuilder.group(
      this.buildFormGroupProps(companyInfo, postPurchaseInfo)
    );
  }

  buildFormGroupValues(
    companyInfo: CompanyInfo,
    postPurchaseInfo: PostPurchaseInfo
  ): any {
    return mapValues(
      this.buildFormGroupProps(companyInfo, postPurchaseInfo),
      (value) => value[0]
    );
  }

  buildFormGroupProps(
    companyInfo: CompanyInfo,
    postPurchaseInfo: PostPurchaseInfo
  ): any {
    const employeesRadio =
      !companyInfo.numOfEmployees ||
      companyInfo.numOfEmployees === NumOfEmployeesEnum._0
        ? EmployeesRadioEnum.just_me
        : EmployeesRadioEnum.there_are_more;

    return {
      [this.FORM_KEYS.INDUSTRY]: [companyInfo.industry, Validators.required],
      [this.FORM_KEYS.SUB_INDUSTRY]: [
        companyInfo.subIndustries,
        Validators.required,
      ],
      [this.FORM_KEYS.BUSINESS_YEARS]: [
        postPurchaseInfo.yearsInBusiness,
        Validators.required,
      ],
      [this.FORM_KEYS.EMPLOYEES_RADIO]: [employeesRadio],
      [this.FORM_KEYS.EMPLOYEES]: [
        companyInfo.numOfEmployees,
        Validators.required,
      ],
      [this.FORM_KEYS.WHAT_MATTERS_MOST]: [
        postPurchaseInfo.priority,
        Validators.required,
      ],
    };
  }

  buildIndustryValues(locale: string): List<ISelectOption> {
    return List(
      this._utilsService
        .getIndustries(locale)
        .map(({ key, value }: { key: string; value: string }) => {
          return {
            key: value,
            value: key,
            trackingValue: key,
          };
        })
    );
  }

  buildSubIndustryValues(
    locale: string,
    industry: string
  ): List<ISelectOption> {
    return List(
      this._utilsService
        .getSubIndustries(locale, industry)
        .map(({ key, value }: { key: string; value: string }) => {
          return {
            key: value,
            value: key,
            trackingValue: key,
          };
        })
    );
  }

  buildBusinessYears(): List<IRadioOption> {
    return List([
      {
        key: this._translateService.instant(
          'DIALOG.POST_PURCHASE.FIELDS.BUSINESS_YEARS.0_TO_1'
        ),
        value: PostPurchaseYearsEnum._0_to_1,
      },
      {
        key: this._translateService.instant(
          'DIALOG.POST_PURCHASE.FIELDS.BUSINESS_YEARS.1_TO_3'
        ),
        value: PostPurchaseYearsEnum._1_to_3,
      },
      {
        key: this._translateService.instant(
          'DIALOG.POST_PURCHASE.FIELDS.BUSINESS_YEARS.3_TO_5'
        ),
        value: PostPurchaseYearsEnum._3_to_5,
      },
      {
        key: this._translateService.instant(
          'DIALOG.POST_PURCHASE.FIELDS.BUSINESS_YEARS.5_OR_MORE'
        ),
        value: PostPurchaseYearsEnum._5_or_more,
      },
    ]);
  }

  buildEmployeesOptions(): List<ISelectOption> {
    return List([
      {
        key: this._translateService.instant(
          'DIALOG.POST_PURCHASE.FIELDS.EMPLOYEES.OPTIONS.1'
        ),
        value: NumOfEmployeesEnum._1,
        trackingValue: NumOfEmployeesEnum._1,
      },
      {
        key: this._translateService.instant(
          'DIALOG.POST_PURCHASE.FIELDS.EMPLOYEES.OPTIONS.2'
        ),
        value: NumOfEmployeesEnum._2,
        trackingValue: NumOfEmployeesEnum._2,
      },
      {
        key: this._translateService.instant(
          'DIALOG.POST_PURCHASE.FIELDS.EMPLOYEES.OPTIONS.3'
        ),
        value: NumOfEmployeesEnum._3,
        trackingValue: NumOfEmployeesEnum._3,
      },
      {
        key: this._translateService.instant(
          'DIALOG.POST_PURCHASE.FIELDS.EMPLOYEES.OPTIONS.4'
        ),
        value: NumOfEmployeesEnum._4,
        trackingValue: NumOfEmployeesEnum._4,
      },
      {
        key: this._translateService.instant(
          'DIALOG.POST_PURCHASE.FIELDS.EMPLOYEES.OPTIONS.5'
        ),
        value: NumOfEmployeesEnum._5,
        trackingValue: NumOfEmployeesEnum._5,
      },
      {
        key: this._translateService.instant(
          'DIALOG.POST_PURCHASE.FIELDS.EMPLOYEES.OPTIONS.6_TO_10'
        ),
        value: NumOfEmployeesEnum._6_to_10,
        trackingValue: NumOfEmployeesEnum._6_to_10,
      },
      {
        key: this._translateService.instant(
          'DIALOG.POST_PURCHASE.FIELDS.EMPLOYEES.OPTIONS.10_TO_15'
        ),
        value: NumOfEmployeesEnum._11_to_15,
        trackingValue: NumOfEmployeesEnum._11_to_15,
      },
      {
        key: this._translateService.instant(
          'DIALOG.POST_PURCHASE.FIELDS.EMPLOYEES.OPTIONS.16_OR_MORE'
        ),
        value: NumOfEmployeesEnum._16_or_more,
        trackingValue: NumOfEmployeesEnum._16_or_more,
      },
    ]);
  }

  buildWhatMattersMostOptions(): List<IRadioOption> {
    return List([
      {
        key: this._translateService.instant(
          'DIALOG.POST_PURCHASE.FIELDS.WHAT_MATTERS_MOST.SAVE_TIME'
        ),
        value: PostPurchasePriorityEnum.save_time,
      },
      {
        key: this._translateService.instant(
          'DIALOG.POST_PURCHASE.FIELDS.WHAT_MATTERS_MOST.STAY_ORGANIZED'
        ),
        value: PostPurchasePriorityEnum.stay_organized,
      },
      {
        key: this._translateService.instant(
          'DIALOG.POST_PURCHASE.FIELDS.WHAT_MATTERS_MOST.GET_PAID_FASTER'
        ),
        value: PostPurchasePriorityEnum.get_paid_faster,
      },
      {
        key: this._translateService.instant(
          'DIALOG.POST_PURCHASE.FIELDS.WHAT_MATTERS_MOST.WIN_MORE_BUSINESS'
        ),
        value: PostPurchasePriorityEnum.win_more_business,
      },
    ]);
  }

  buildSettingsProps(postPurchaseInfo: Map<string, any>, isCompleted: boolean) {
    const formKeys = this.FORM_KEYS;
    const subIndustries = List<string>(
      postPurchaseInfo.get(formKeys.SUB_INDUSTRY) || []
    );

    let dateCompleted;
    let state;

    if (isCompleted) {
      dateCompleted = this._dateTimeService.getNow(null, true);
      state = PostPurchaseStateEnum.completed;
    }

    return fromJS({
      [SettingsContent.KEYS.COMPANY_INFO]: {
        [CompanyInfo.KEYS.INDUSTRY]: postPurchaseInfo.get(formKeys.INDUSTRY),
        [CompanyInfo.KEYS.SUB_INDUSTRIES]: subIndustries.toArray(),
        [CompanyInfo.KEYS.NUM_OF_EMPLOYEES]: postPurchaseInfo.get(
          formKeys.EMPLOYEES,
          NumOfEmployeesEnum._0
        ),
      },
      [SettingsContent.KEYS.POST_PURCHASE_INFO]: {
        [PostPurchaseInfo.KEYS.YEARS_IN_BUSINESS]: postPurchaseInfo.get(
          formKeys.BUSINESS_YEARS
        ),
        [PostPurchaseInfo.KEYS.PRIORITY]: postPurchaseInfo.get(
          formKeys.WHAT_MATTERS_MOST
        ),
        [PostPurchaseInfo.KEYS.DATE_COMPLETED]: dateCompleted,
        [PostPurchaseInfo.KEYS.STATE]: state,
      },
    });
  }

  buildTrackingAdditionalProperties(postPurchaseInfo: Map<string, any>): any {
    const formKeys = this.FORM_KEYS;
    const props = {
      industry: postPurchaseInfo.get(formKeys.INDUSTRY),
      sub_industries: postPurchaseInfo.get(formKeys.SUB_INDUSTRY),
      years_business: postPurchaseInfo.get(formKeys.BUSINESS_YEARS),
      employees: postPurchaseInfo.get(formKeys.EMPLOYEES),
      what_matters_most_to_you: postPurchaseInfo.get(
        formKeys.WHAT_MATTERS_MOST
      ),
    };

    return pickBy(props, identity);
  }
}
